'use client';

import React, { useEffect } from 'react';
import { useRouter } from 'next/navigation';

export default function TallyBookingForm() {
  const router = useRouter();

  useEffect(() => {
    // Load Tally embed script
    const script = document.createElement('script');
    script.src = 'https://tally.so/widgets/embed.js';
    script.async = true;
    document.body?.appendChild(script);

    // Listen for Tally form submission event
    const handleMessage = (e: MessageEvent) => {
      if (typeof e?.data === 'string' && e.data.includes('Tally.FormSubmitted')) {
        try {
          const parsed = JSON.parse(e.data);
          const payload = parsed?.payload;

          // Build a short reference from the submission ID
          const submissionId = payload?.id || '';
          const ref = submissionId
            ? `LPR-${submissionId.slice(0, 8).toUpperCase()}`
            : `LPR-${Date.now().toString(36).toUpperCase()}`;

          // Extract common fields from the submission if available
          const fields: Array<{ title: string; answer: { value: string } }> = payload?.fields || [];

          const findField = (keywords: string[]) => {
            const field = fields.find((f) =>
              keywords.some((kw) => f?.title?.toLowerCase().includes(kw.toLowerCase()))
            );
            return field?.answer?.value || '';
          };

          const name = findField(['name', 'full name', 'your name']);
          const email = findField(['email']);
          const brand = findField(['brand', 'make', 'manufacturer']);
          const model = findField(['model']);
          const repair = findField(['repair', 'issue', 'problem', 'service']);
          const price = findField(['price', 'cost', 'quote']);
          const serviceType = findField(['service type', 'drop', 'mail', 'delivery']) || 'drop-off';

          const params = new URLSearchParams({ ref });
          if (name) params.set('name', name);
          if (email) params.set('email', email);
          if (brand) params.set('brand', brand);
          if (model) params.set('model', model);
          if (repair) params.set('repair', repair);
          if (price) params.set('price', price);
          if (serviceType) params.set('serviceType', serviceType);

          router.push(`/repairs/confirmation?${params.toString()}`);
        } catch {
          // Fallback: redirect with a generated reference
          const ref = `LPR-${Date.now().toString(36).toUpperCase()}`;
          router.push(`/repairs/confirmation?ref=${ref}`);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      document.body?.removeChild(script);
      window.removeEventListener('message', handleMessage);
    };
  }, [router]);

  return (
    <section id="book" className="py-16 lg:py-20 bg-background">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="section-label">Booking</p>
          <h2 className="section-title mb-4">Book a Repair</h2>
          <p className="section-subtitle max-w-xl mx-auto">
            Fill in the form below to book your repair. We&apos;ll be in touch within 1 business day.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="card-base overflow-hidden">
            <iframe
              data-tally-src="https://tally.so/embed/lbxXOV?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1"
              loading="lazy"
              width="100%"
              height="500"
              frameBorder={0}
              marginHeight={0}
              marginWidth={0}
              title="Repair Booking Form"
              className="w-full"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
